import React, { useEffect, useState, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import Notification from '../../Notification/Notification';
import { LogOut, User, Award, ChevronDown } from 'lucide-react';
import officeInfoService from '../../../services/OfficeInfoService';
import {
  PROFILE_PICTURE_CHANGED_EVENT,
  publishProfilePictureUrl,
  readProfilePictureUrl
} from '../../../utils/profilePicture';

export default function NavMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [profilePictureUrl, setProfilePictureUrl] = useState(readProfilePictureUrl);
  const history = useHistory();
  const menuRef = useRef(null);

  useEffect(() => {
    // eslint-disable-next-line no-undef
    setName(localStorage.getItem('name'));
    // eslint-disable-next-line no-undef
    setRole(localStorage.getItem('role') || 'User');

    const handleProfilePictureChange = (event) => {
      setProfilePictureUrl(event.detail || '');
    };

    const fetchProfilePicture = async () => {
      // eslint-disable-next-line no-undef
      const employeeId = localStorage.getItem('userId');
      if (!employeeId) return;

      try {
        const response = await officeInfoService.getOfficeInfo({ employeeId });
        const officeInfo = response?.data?.data || {};
        publishProfilePictureUrl(officeInfo.useAsProfilePicture ? officeInfo.profilePictureUrl : '');
      } catch (error) {
        // Keep the locally cached image if profile security data is temporarily unavailable.
      }
    };

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // eslint-disable-next-line no-undef
    document.addEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line no-undef
    window.addEventListener(PROFILE_PICTURE_CHANGED_EVENT, handleProfilePictureChange);
    fetchProfilePicture();
    // eslint-disable-next-line no-undef
    return () => {
      // eslint-disable-next-line no-undef
      document.removeEventListener('mousedown', handleClickOutside);
      // eslint-disable-next-line no-undef
      window.removeEventListener(PROFILE_PICTURE_CHANGED_EVENT, handleProfilePictureChange);
    };
  }, []);

  const handleLogout = (event) => {
    event.preventDefault();
    // eslint-disable-next-line no-undef
    localStorage.removeItem('token');
    // eslint-disable-next-line no-undef
    localStorage.removeItem('refreshToken');
    // eslint-disable-next-line no-undef
    localStorage.removeItem('name');
    // eslint-disable-next-line no-undef
    localStorage.removeItem('role');
    publishProfilePictureUrl('');
    history.push('/login');
    // eslint-disable-next-line no-undef
    window.location.reload();
  };

  return (
    <div className="tw-flex tw-items-center tw-space-x-4">
      <Link
        to="/notification"
        className="tw-p-2 tw-text-gray-400 hover:tw-bg-gray-50 tw-rounded-xl tw-transition-colors"
      >
        <Notification />
      </Link>

      <div className="tw-relative" ref={menuRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="tw-flex tw-items-center tw-space-x-3 tw-p-1 tw-rounded-2xl hover:tw-bg-gray-50 tw-transition-colors focus:tw-outline-none"
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <div className="tw-text-right tw-hidden md:tw-block">
            <div className="tw-text-sm tw-font-bold tw-text-gray-900 tw-leading-none tw-mb-1">{name || 'User'}</div>
            <div className="tw-text-[10px] tw-font-medium tw-text-gray-400 tw-mt-1">{role}</div>
          </div>
          <div className="tw-h-10 tw-w-10 tw-rounded-xl tw-bg-blue-100 tw-flex tw-items-center tw-justify-center tw-text-blue-600 tw-font-bold tw-text-sm tw-overflow-hidden tw-border-2 tw-border-white">
            {profilePictureUrl ? (
              <img
                src={profilePictureUrl}
                alt={`${name || 'User'} profile`}
                className="tw-h-full tw-w-full tw-object-cover"
                onError={() => publishProfilePictureUrl('')}
              />
            ) : name ? (
              name.charAt(0).toUpperCase()
            ) : (
              'U'
            )}
          </div>
          <ChevronDown
            className={`tw-h-4 tw-w-4 tw-text-gray-400 tw-transition-transform tw-duration-200 ${
              isOpen ? 'tw-rotate-180' : ''
            }`}
          />
        </button>

        {isOpen && (
          <div className="tw-absolute tw-right-0 tw-mt-3 tw-w-56 tw-rounded-2xl tw-shadow-xl tw-bg-white tw-ring-1 tw-ring-black/5 tw-z-50 tw-py-2 tw-transition-all tw-duration-200 tw-ease-out">
            <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-50">
              <p className="tw-text-sm tw-font-bold tw-text-gray-900 tw-truncate">{name || 'User'}</p>
              <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">{role}</p>
            </div>

            <Link
              to="/myprofile"
              className="tw-flex tw-items-center tw-px-4 tw-py-2.5 tw-text-sm tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <div className="tw-p-1.5 tw-bg-gray-100 tw-rounded-xl tw-mr-3">
                <User className="tw-h-4 tw-w-4 tw-text-gray-500" />
              </div>
              Profile
            </Link>

            <Link
              to="/myqualification"
              className="tw-flex tw-items-center tw-px-4 tw-py-2.5 tw-text-sm tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <div className="tw-p-1.5 tw-bg-gray-100 tw-rounded-xl tw-mr-3">
                <Award className="tw-h-4 tw-w-4 tw-text-gray-500" />
              </div>
              Qualification
            </Link>

            <div className="tw-my-1 tw-border-t tw-border-gray-50"></div>

            <button
              onClick={handleLogout}
              className="tw-flex tw-w-full tw-items-center tw-px-4 tw-py-2.5 tw-text-sm tw-text-red-500 hover:tw-bg-red-50 tw-transition-colors"
            >
              <div className="tw-p-1.5 tw-bg-red-50 tw-rounded-xl tw-mr-3">
                <LogOut className="tw-h-4 tw-w-4 tw-text-red-400" />
              </div>
              Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
